import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';

type AuditAction =
  | 'login'
  | 'logout'
  | 'patient_view'
  | 'patient_create'
  | 'patient_update'
  | 'patient_delete'
  | 'gncp_save'
  | 'document_upload'
  | 'document_delete'
  // Zugriff auf ein von der Patient:in im Portal eingestelltes Dokument.
  // Eigene Aktion, weil es eine Verarbeitung fremd-eingebrachter Art.-9-
  // Daten ist und im Verarbeitungsverzeichnis unterscheidbar sein muss.
  | 'document_download'
  | 'dsgvo_export'
  | 'dsgvo_purge'
  | 'consent_change'
  | 'appointment_create'
  | 'appointment_update'
  | 'invoice_create'
  | 'session_recording'
  | 'rezept_analysis'
  | 'fhir_export'
  // Phase 1 — neue Actions
  | 'screening_create'
  | 'hmv_create'
  | 'video_start'
  | 'data_export_csv'
  | 'billing_create'
  | 'data_breach_report'
  // F-13-06 (audit): settings + admin-import actions previously
  // missing from the Art. 30 record-of-processing log.
  | 'settings.praxis.update'
  | 'settings.email.update'
  | 'settings.profile.update'
  | 'settings.nomenklatur.update'
  | 'settings.workspace.update'
  | 'consent.research.grant'
  | 'consent.research.revoke'
  | 'import.bls.start'
  | 'import.bls.complete'
  | 'import.leitlinien.start'
  | 'import.leitlinien.complete'
  // F-10-03: invoice lifecycle audit
  | 'invoice_sent'
  | 'invoice_draft';

interface AuditOptions {
  patientId?: string | null;
  resourceType?: string;
  resourceId?: string;
  dataCategories?: string[];
  oldValues?: Record<string, unknown>;
  newValues?: Record<string, unknown>;
}

/**
 * Schreibt einen Audit-Log-Eintrag (DSGVO Art. 30 - Verarbeitungsverzeichnis).
 * Fire-and-forget: Fehler werden geloggt aber nicht propagiert,
 * damit die eigentliche Aktion nicht blockiert wird.
 */
export async function logAudit(
  action: AuditAction,
  userId: string | null,
  organizationId: string | null,
  details?: Record<string, unknown>,
  options?: AuditOptions,
) {
  try {
    await supabase.from('audit_logs').insert([{
      action,
      user_id: userId,
      organization_id: organizationId,
      details: (details ?? {}) as Json,
      patient_id: options?.patientId ?? null,
      resource_type: options?.resourceType ?? null,
      resource_id: options?.resourceId ?? null,
      data_categories: options?.dataCategories ?? null,
      old_values: (options?.oldValues ?? null) as Json | null,
      new_values: (options?.newValues ?? null) as Json | null,
    }]);
  } catch (err) {
    console.error('[Audit] Failed to write audit log:', err);
  }
}

/**
 * Sanitize CSV cell value to prevent formula injection (DDE attacks).
 * Prefixes dangerous characters with a single quote so Excel treats them as text.
 */
function sanitizeCsvCell(value: unknown): string {
  const str = String(value ?? '');
  // Prevent formula injection: cells starting with =, +, -, @, tab, CR
  if (/^[=+\-@\t\r]/.test(str)) return "'" + str;
  return str;
}

/**
 * CSV-Export der Audit-Logs fuer den Datenschutzbeauftragten.
 */
export function exportAuditLogsCsv(logs: Array<Record<string, unknown>>): string {
  const headers = ['Zeitpunkt', 'Aktion', 'Benutzer', 'Patient-ID', 'Ressource', 'Rechtsgrundlage', 'Details'];
  const rows = logs.map(log => [
    log.created_at ?? '',
    log.action ?? '',
    log.user_email ?? log.user_id ?? '',
    log.patient_id ?? '',
    `${log.resource_type ?? ''}${log.resource_id ? ':' + log.resource_id : ''}`,
    log.legal_basis ?? 'Art. 9 Abs. 2 lit. h DSGVO',
    log.details ? JSON.stringify(log.details) : '',
  ]);

  const csvContent = [
    headers.join(';'),
    ...rows.map(row => row.map(cell => `"${sanitizeCsvCell(cell).replace(/"/g, '""')}"`).join(';')),
  ].join('\n');

  return csvContent;
}

export function downloadCsv(csv: string, filename: string) {
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
